import React, { useState } from 'react';
import { AxiosError } from 'axios';

import { toE164 } from 'utils/phone';

import {
  initiateSubscription,
  requestSubscribeOtp,
  verifySubscribeOtp,
} from './api';
import { SubscribeScene } from './scene';

const SESSION_KEY = 'kudable_subscribe';

function loadSession(): {
  step: Account.Step;
  countryCode: string;
  localNumber: string;
  provider: Account.FoundProvider | null;
} {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { step: 'phone', countryCode: '+1', localNumber: '', provider: null };
}

function saveSession(data: {
  step: Account.Step;
  countryCode: string;
  localNumber: string;
  provider: Account.FoundProvider | null;
}) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {}
}

function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

export default function Subscribe() {
  const saved = loadSession();

  const [step, setStep] = useState<Account.Step>(saved.step);
  const [countryCode, setCountryCode] = useState(saved.countryCode);
  const [localNumber, setLocalNumber] = useState(saved.localNumber);
  const [otpCode, setOtpCode] = useState('');
  const [months, setMonths] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<Account.FoundProvider | null>(
    saved.provider,
  );

  const fullPhone = toE164(countryCode, localNumber);

  const persist = (
    updates: Partial<{
      step: Account.Step;
      countryCode: string;
      localNumber: string;
      provider: Account.FoundProvider | null;
    }>,
  ) => {
    const next = { step, countryCode, localNumber, provider, ...updates };
    saveSession(next);
  };

  const handleCountryCodeChange = (value: string) => {
    setCountryCode(value);
    persist({ countryCode: value });
  };

  const handleLocalNumberChange = (value: string) => {
    setLocalNumber(value);
    persist({ localNumber: value });
  };

  const handleRequestOtp = () => {
    if (!localNumber.trim()) return;
    setError(null);
    setIsLoading(true);

    requestSubscribeOtp(fullPhone)
      .then((response) => {
        setProvider(response.data);
        setStep('otp');
        persist({ step: 'otp', provider: response.data });
      })
      .catch((requestError: AxiosError) => {
        if (requestError.response?.status === 404) {
          setError(
            'No account found with that phone number. Please check and try again.',
          );
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleVerifyOtp = () => {
    if (otpCode.length !== 6) return;
    setError(null);
    setIsLoading(true);

    verifySubscribeOtp(fullPhone, otpCode)
      .then((response) => {
        setProvider(response.data);
        setStep('confirm');
        persist({ step: 'confirm', provider: response.data });
      })
      .catch((verifyError: AxiosError<{ error?: string }>) => {
        const code = verifyError.response?.data?.error ?? '';
        if (code.includes('expired')) {
          setError(
            'That code has expired. Please go back and request a new one.',
          );
        } else if (verifyError.response?.status === 422) {
          setError('Incorrect code. Please check and try again.');
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setOtpCode('');
    setError(null);
    persist({ step: 'phone' });
  };

  const handlePay = () => {
    setError(null);
    setIsLoading(true);

    initiateSubscription(fullPhone, months)
      .then((response) => {
        clearSession();
        window.location.href = response.data.payment_url;
      })
      .catch(() => setIsLoading(false));
  };

  return (
    <SubscribeScene
      step={step}
      countryCode={countryCode}
      localNumber={localNumber}
      otpCode={otpCode}
      months={months}
      isLoading={isLoading}
      error={error}
      provider={provider}
      onCountryCodeChange={handleCountryCodeChange}
      onLocalNumberChange={handleLocalNumberChange}
      onOtpCodeChange={setOtpCode}
      onMonthsChange={setMonths}
      onRequestOtp={handleRequestOtp}
      onVerifyOtp={handleVerifyOtp}
      onBackToPhone={handleBackToPhone}
      onPay={handlePay}
    />
  );
}
